import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { Groupe } from '../models/groupe.model';
import { GroupeCreateRequest } from '../requests/groupe-create-request';
import { GroupeUpdateRequest } from '../requests/groupe-update-request';
import { GroupeService } from '../services/groupe.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';

export function avecMéthodesGroupes() {
  return signalStoreFeature(
    // Updater
    withMethods((store) => ({
      chargerGroupes: (state: { groupes: Groupe[] }) => patchState(store, setAllEntities(state.groupes)),
      ajouterGroupe: (state: { groupe: Groupe }) => patchState(store, addEntity(state.groupe)),
      modifierGroupe: (state: { groupe: Groupe }) =>
        patchState(store, updateEntity({ id: state.groupe.id, changes: state.groupe })),
      supprimerGroupe: (id: string) => patchState(store, removeEntity(id)),
    })),

    // Effects
    withMethods((store, groupeService = inject(GroupeService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idRéférentiel) =>
            groupeService.findAll(idRéférentiel).pipe(
              tapResponse({
                next: (result) => store.chargerGroupes({ groupes: result.items }),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des groupes, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: GroupeCreateRequest; next: () => void }>(
        pipe(
          switchMap(({ data, next }) =>
            groupeService.créer(data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création du groupe, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{ id: string; data: GroupeUpdateRequest; next?: () => void }>(
        pipe(
          switchMap(({ id, data, next }) =>
            groupeService.modifier(id, data).pipe(
              tapResponse({
                next: () => {
                  store.modifierGroupe({ groupe: { id, ...data } });
                  next?.();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification du groupe, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<string>(
        pipe(
          switchMap((idGroupe) =>
            groupeService.supprimer(idGroupe).pipe(
              tapResponse({
                next: () => store.supprimerGroupe(idGroupe),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression du groupe, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
